import { computed, effect, Injectable, signal } from '@angular/core';
import { WindowRef } from '@shared/tools/window';
import {
  PRODUCT_LIST_FIELD,
  ProductListColumnConfig,
  productListDefaultColumns,
} from '@b2b/features/product/components/products-table/products-table.types';
import { STORAGE_KEYS } from '@shared/tools/storage-keys';

@Injectable({
  providedIn: 'root',
})
export class ProductsTableService {
  private readonly STORAGE_KEY = STORAGE_KEYS.b2b.productTableColumns;
  private readonly _columns = signal(productListDefaultColumns);

  columns = computed(() => this._columns());
  activeColumns = computed(() => this._columns().filter(column => column.enabled));
  invoiceAmountLabel = signal('($) NF');

  setInvoiceAmountLabel(isTaxCalculated: boolean) {
    this.invoiceAmountLabel.set(isTaxCalculated ? '($) NF' : '($) FÁBRICA - DESC');
  }

  constructor(private winRef: WindowRef) {
    if (this.winRef.isBrowser()) {
      const saved = this.winRef.localStorage?.getItem(this.STORAGE_KEY);
      if (saved) {
        try {
          const parsed: ProductListColumnConfig[] = JSON.parse(saved);

          const restored = parsed.map(col => {
            const label = productListDefaultColumns.find(c => c.field === col.field)?.label || col.label;
            return { ...col, label };
          });

          const merged = [
            ...restored,
            ...productListDefaultColumns.filter(c => !restored.some(r => r.field === c.field)),
          ];

          this._columns.set(merged);
        } catch {
          this._columns.set(productListDefaultColumns);
        }
      }
    }

    effect(() => {
      if (this.winRef.isBrowser()) {
        this.winRef.localStorage?.setItem(this.STORAGE_KEY, JSON.stringify(this._columns()));
      }
    });
  }

  enableColumn(field: PRODUCT_LIST_FIELD, enabled = true) {
    this._columns.update(cols => cols.map(col => (col.field === field ? { ...col, enabled } : col)));
  }

  reorderColumn(currentIndex: number, newIndex: number) {
    this._columns.update(cols => {
      if (currentIndex < 0 || currentIndex >= cols.length || newIndex < 0 || newIndex >= cols.length) {
        return cols;
      }

      const updated = [...cols];
      const [moved] = updated.splice(currentIndex, 1);
      updated.splice(newIndex, 0, moved);

      return updated;
    });
  }
}
